import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import DynamicSelect from "../DynamicSelect";
import PageHeading from "../PageHeading";

const API_URL = "https://api.muktihospital.com/api/diagnostic-page";
const API_KEY =
  "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";

const EMPTY = {
  metaTitle: "",
  metaDescription: "",
  heroTitle: "",
  heroDesc: "",
  heroBtn1Text: "",
  heroBtn1Link: "",
  heroBtn2Text: "",
  heroBtn2Link: "",
  callAreaTitle: "",
  callAreaSubtitle: "",
  listTitle: "",
  listSubtitle: "",
  rightCareTitle: "",
  rightCareDesc: "",
  whyChooseTitle: "",
  emergencyCare: "",
  emergencyCareDesc: "",
  expertDoctors: "",
  expertDoctorsDesc: "",
  advancedImaging: "",
  advancedImagingDesc: "",
  hassleFreeBooking: "",
  hassleFreeBookingDesc: "",
  emergencyCall: "",
  emergencyPhone: "",
};

const DiagnosticPage = () => {
  const [language, setLanguage] = useState("en");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Keep BOTH languages in memory so switching never wipes unsaved edits
  const [allForms, setAllForms] = useState({ en: { ...EMPTY }, bn: { ...EMPTY } });
  const [heroImage, setHeroImage] = useState(null); // new File to upload
  const [existingImage, setExistingImage] = useState(null);

  const languageOptions = [
    { value: "en", label: "English" },
    { value: "bn", label: "Bangla" },
  ];

  // Load the FULL record once (both languages), no reload on language switch
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await axios.get(API_URL, { headers: { "x-api-key": API_KEY } });
        const translations = res.data?.translations || {};

        // Only take fields that actually exist for each language — no cross-fallback
        const pick = (obj = {}) => ({
          metaTitle: obj.metaTitle || "",
          metaDescription: obj.metaDescription || "",
          heroTitle: obj.heroTitle || "",
          heroDesc: obj.heroDesc || "",
          heroBtn1Text: obj.heroBtn1Text || "",
          heroBtn1Link: obj.heroBtn1Link || "",
          heroBtn2Text: obj.heroBtn2Text || "",
          heroBtn2Link: obj.heroBtn2Link || "",
          callAreaTitle: obj.callAreaTitle || "",
          callAreaSubtitle: obj.callAreaSubtitle || "",
          listTitle: obj.listTitle || "",
          listSubtitle: obj.listSubtitle || "",
          rightCareTitle: obj.rightCareTitle || "",
          rightCareDesc: obj.rightCareDesc || "",
          whyChooseTitle: obj.whyChooseTitle || "",
          emergencyCare: obj.emergencyCare || "",
          emergencyCareDesc: obj.emergencyCareDesc || "",
          expertDoctors: obj.expertDoctors || "",
          expertDoctorsDesc: obj.expertDoctorsDesc || "",
          advancedImaging: obj.advancedImaging || "",
          advancedImagingDesc: obj.advancedImagingDesc || "",
          hassleFreeBooking: obj.hassleFreeBooking || "",
          hassleFreeBookingDesc: obj.hassleFreeBookingDesc || "",
          emergencyCall: obj.emergencyCall || "",
          emergencyPhone: obj.emergencyPhone || "",
        });

        setAllForms({
          en: pick(translations.en),
          bn: pick(translations.bn),
        });

        // Image is shared; read it from whichever language has it
        const img =
          translations.en?.heroImage || translations.bn?.heroImage || null;
        setExistingImage(img);
      } catch (err) {
        console.error("Error loading diagnostic hero:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []); // ← runs ONCE, not on language change

  // Current language's form
  const form = allForms[language];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setAllForms((prev) => ({
      ...prev,
      [language]: { ...prev[language], [name]: value },
    }));
  };

  const handleLanguageChange = (opt) => setLanguage(opt.value); // no API call → no wipe

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append("language", language);
      Object.entries(form).forEach(([key, value]) => formData.append(key, value));
      if (heroImage instanceof File) formData.append("heroImage", heroImage);

      const res = await axios.post(API_URL, formData, {
        headers: { "x-api-key": API_KEY, "Content-Type": "multipart/form-data" },
      });

      // Refresh the saved image path if the server returned one
      const saved = res.data?.data?.[language];
      if (saved?.heroImage) setExistingImage(saved.heroImage);
      setHeroImage(null);

      toast.success(`Diagnostic hero saved (${language})`);
    } catch (err) {
      console.error("Error saving diagnostic hero:", err);
      toast.error("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const breadcrumbs = [
    { label: "Dashboard", url: "/" },
    { label: "Manage Sections" },
    { label: "Diagnostic Page" },
  ];

  const previewSrc =
    heroImage instanceof File
      ? URL.createObjectURL(heroImage)
      : existingImage
      ? `https://api.muktihospital.com${existingImage}`
      : null;

  return (
    <div>
      <ToastContainer position="top-right" />
      <PageHeading title="Diagnostic Page" breadcrumbs={breadcrumbs} />

      <div className="mb-4 flex justify-end">
        <DynamicSelect
          options={languageOptions}
          onChange={handleLanguageChange}
          value={languageOptions.find((o) => o.value === language)}
          isSearchable
        />
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white shadow-sm rounded-lg">
          <h2 className="text-base font-medium text-gray-700 border-b border-dashed p-5">
            SEO ({language === "en" ? "English" : "Bangla"})
          </h2>
          <div className="p-5">
            <label className="label">Meta Title</label>
            <input
              type="text"
              name="metaTitle"
              value={form.metaTitle}
              onChange={handleChange}
              className="input-field"
              placeholder="Leave empty to use default"
            />
          </div>
          <div className="p-5 pt-0">
            <label className="label">Meta Description</label>
            <textarea
              name="metaDescription"
              rows={2}
              value={form.metaDescription}
              onChange={handleChange}
              className="input-field"
              placeholder="Leave empty to use default"
            />
          </div>
        </div>

        <div className="bg-white shadow-sm rounded-lg">
          <h2 className="text-base font-medium text-gray-700 border-b border-dashed p-5">
            Hero Section ({language === "en" ? "English" : "Bangla"})
          </h2>

          <div className="p-5">
            <label className="label">Hero Title</label>
            <input
              type="text"
              name="heroTitle"
              value={form.heroTitle}
              onChange={handleChange}
              className="input-field"
              placeholder="Leave empty to use default"
            />
          </div>

          <div className="p-5 pt-0">
            <label className="label">Hero Description</label>
            <textarea
              name="heroDesc"
              rows={3}
              value={form.heroDesc}
              onChange={handleChange}
              className="input-field"
              placeholder="Leave empty to use default"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-5 pt-0">
            <div>
              <label className="label">Button 1 Text</label>
              <input type="text" name="heroBtn1Text" value={form.heroBtn1Text} onChange={handleChange} className="input-field" placeholder="e.g. Call Us Now" />
            </div>
            <div>
              <label className="label">Button 1 Link</label>
              <input type="text" name="heroBtn1Link" value={form.heroBtn1Link} onChange={handleChange} className="input-field" placeholder="e.g. /contact" />
            </div>
            <div>
              <label className="label">Button 2 Text</label>
              <input type="text" name="heroBtn2Text" value={form.heroBtn2Text} onChange={handleChange} className="input-field" placeholder="e.g. View Offers" />
            </div>
            <div>
              <label className="label">Button 2 Link</label>
              <input type="text" name="heroBtn2Link" value={form.heroBtn2Link} onChange={handleChange} className="input-field" placeholder="e.g. #all-tests" />
            </div>
          </div>

          <div className="p-5 pt-0">
            <label className="label">Hero Image (shared for both languages)</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setHeroImage(e.target.files?.[0] || null)}
              className="border p-2 rounded w-full"
            />
            {previewSrc && (
              <img
                src={previewSrc}
                alt="Hero preview"
                className="mt-3 max-w-[300px] rounded border"
              />
            )}
          </div>
        </div>

        <div className="bg-white shadow-sm rounded-lg">
          <h2 className="text-base font-medium text-gray-700 border-b border-dashed p-5">
            Call Area &amp; Test List ({language === "en" ? "English" : "Bangla"})
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-5">
            <div>
              <label className="label">Call Area Title</label>
              <input type="text" name="callAreaTitle" value={form.callAreaTitle} onChange={handleChange} className="input-field" placeholder="Leave empty to use default" />
            </div>
            <div>
              <label className="label">Call Area Subtitle</label>
              <input type="text" name="callAreaSubtitle" value={form.callAreaSubtitle} onChange={handleChange} className="input-field" placeholder="Leave empty to use default" />
            </div>
            <div>
              <label className="label">Test List Title</label>
              <input type="text" name="listTitle" value={form.listTitle} onChange={handleChange} className="input-field" placeholder="Leave empty to use default" />
            </div>
            <div>
              <label className="label">Test List Subtitle</label>
              <input type="text" name="listSubtitle" value={form.listSubtitle} onChange={handleChange} className="input-field" placeholder="Leave empty to use default" />
            </div>
          </div>
        </div>

        <div className="bg-white shadow-sm rounded-lg">
          <h2 className="text-base font-medium text-gray-700 border-b border-dashed p-5">
            Right Care &amp; Why Choose ({language === "en" ? "English" : "Bangla"})
          </h2>
          <div className="p-5">
            <label className="label">Right Care Title</label>
            <input type="text" name="rightCareTitle" value={form.rightCareTitle} onChange={handleChange} className="input-field" placeholder="Leave empty to use default" />
          </div>
          <div className="p-5 pt-0">
            <label className="label">Right Care Description</label>
            <textarea name="rightCareDesc" rows={2} value={form.rightCareDesc} onChange={handleChange} className="input-field" placeholder="Leave empty to use default" />
          </div>
          <div className="p-5 pt-0">
            <label className="label">Why Choose Title</label>
            <input type="text" name="whyChooseTitle" value={form.whyChooseTitle} onChange={handleChange} className="input-field" placeholder="Leave empty to use default" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t p-5">
            <div>
              <label className="label">Point 1 Title</label>
              <input type="text" name="emergencyCare" value={form.emergencyCare} onChange={handleChange} className="input-field" placeholder="e.g. 24/7 Emergency Care" />
            </div>
            <div>
              <label className="label">Point 1 Description</label>
              <input type="text" name="emergencyCareDesc" value={form.emergencyCareDesc} onChange={handleChange} className="input-field" />
            </div>
            <div>
              <label className="label">Point 2 Title</label>
              <input type="text" name="expertDoctors" value={form.expertDoctors} onChange={handleChange} className="input-field" placeholder="e.g. Expert Doctors" />
            </div>
            <div>
              <label className="label">Point 2 Description</label>
              <input type="text" name="expertDoctorsDesc" value={form.expertDoctorsDesc} onChange={handleChange} className="input-field" />
            </div>
            <div>
              <label className="label">Point 3 Title</label>
              <input type="text" name="advancedImaging" value={form.advancedImaging} onChange={handleChange} className="input-field" placeholder="e.g. Advanced Imaging" />
            </div>
            <div>
              <label className="label">Point 3 Description</label>
              <input type="text" name="advancedImagingDesc" value={form.advancedImagingDesc} onChange={handleChange} className="input-field" />
            </div>
            <div>
              <label className="label">Point 4 Title</label>
              <input type="text" name="hassleFreeBooking" value={form.hassleFreeBooking} onChange={handleChange} className="input-field" placeholder="e.g. Hassle-Free Booking" />
            </div>
            <div>
              <label className="label">Point 4 Description</label>
              <input type="text" name="hassleFreeBookingDesc" value={form.hassleFreeBookingDesc} onChange={handleChange} className="input-field" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t p-5">
            <div>
              <label className="label">Emergency Call Title</label>
              <input type="text" name="emergencyCall" value={form.emergencyCall} onChange={handleChange} className="input-field" placeholder="e.g. Need Emergency Help?" />
            </div>
            <div>
              <label className="label">Emergency Phone</label>
              <input type="text" name="emergencyPhone" value={form.emergencyPhone} onChange={handleChange} className="input-field" placeholder="e.g. +880 1601 666-893" />
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving || loading}
            className="bg-M-primary-color/90 text-sm text-white py-2 px-6 rounded-md hover:bg-M-primary-color transition-all duration-200"
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default DiagnosticPage;